/**
 * React Query hook for backend positions.
 * 
 * Uses the centralized account store for state management.
 * - Reads positions from store (populated by useAccountSync)
 * - Mutations use OPTIMISTIC updates (no loading states, instant UI feedback)
 * - WebSocket updates will sync the final state
 */

import { useMemo, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { OrdersApi, PositionsApi, AccountsApi } from "../api-client";
import { toBackendOrderRequest } from "../types";
import { useAccountStore, type StorePosition } from "../store";

// Result type for close position - matches TradingEngine.ClosePositionResult
export interface ClosePositionResult {
  success: boolean;
  realizedPnl?: number;
  txSignature?: string;
  error?: string;
}

// Data needed to close a position - passed from UI, no store lookup required
// This follows the industry-standard pattern: if the user can see it, they can act on it
export interface ClosePositionData {
  market: string;
  side: "long" | "short";
  size: number;
  currentPrice: number;
  entryPrice: number;
  leverage: number;
  margin: number;
}

export const useBackendPositions = (userId: string | undefined) => {
  // Get store actions using getState() - stable references that don't cause re-renders
  const applyMutationResult = useAccountStore.getState().applyMutationResult;
  const removePosition = useAccountStore.getState().removePosition;
  
  // Read positions from centralized store (raw data)
  const positionsMap = useAccountStore((state) => state.positions);
  const isInitialized = useAccountStore((state) => state.isInitialized);
  const isLoading = useAccountStore((state) => state.isLoading);

  // Derive the filtered positions list
  const positions = useMemo(() => {
    return Array.from(positionsMap.values())
      .filter(p => p.status === "open" && p.size > 0);
  }, [positionsMap]);

  // Close position - OPTIMISTIC update (instant UI, no loading state)
  // Handles both partial and full closes
  // Industry-standard pattern: receives data directly from UI, no store lookup needed
  const closePositionMutation = useMutation({
    mutationFn: async (data: ClosePositionData): Promise<ClosePositionResult> => {
      if (!userId) {
        return { success: false, error: "User ID required" };
      }

      // NO STORE LOOKUP - data comes directly from the UI that's displaying the position
      // If the user can see it, they can act on it (Binance/Bybit/HyperLiquid pattern)

      // OPTIMISTIC UPDATE: Check if partial or full close
      const currentPosition = positionsMap.get(data.market);
      const isPartialClose = currentPosition && data.size < currentPosition.size * 0.9999; // Allow for floating point rounding
      
      if (isPartialClose && currentPosition) {
        // Partial close - update position with reduced size
        const newSize = currentPosition.size - data.size;
        const sizeRatio = newSize / currentPosition.size;
        const newMargin = currentPosition.margin * sizeRatio;
        
        // Scale unrealized PnL proportionally to remaining size
        // If closing 50%, remaining PnL should be ~50% of original
        const newUnrealizedPnl = currentPosition.unrealizedPnl * sizeRatio;
        
        // Preserve side sign: backend uses negative size for shorts.
        // The store's `size` is always positive (absolute), so we must
        // re-apply the sign based on the position's side before sending
        // to updatePositionFromHttp, which calls toStorePosition(size >= 0 ? "long" : "short").
        const signedSize = currentPosition.side === "short" ? -newSize : newSize;
        
        // Build Position object for store update (backend API format with strings)
        const updatedPosition = {
          user_id: userId,
          market: data.market,
          size: signedSize.toString(),
          entry_price: currentPosition.entryPrice.toString(),
          mark_price: currentPosition.currentPrice.toString(),
          unrealized_pnl: newUnrealizedPnl.toString(),
          realized_pnl: currentPosition.realizedPnl.toString(),
          leverage: currentPosition.leverage.toString(),
          liquidation_price: currentPosition.liquidationPrice.toString(),
          margin: newMargin.toString(),
          opened_at: currentPosition.openedAt.toISOString(),
          updated_at: new Date().toISOString(),
          take_profit: currentPosition.takeProfit?.toString() ?? null,
          stop_loss: currentPosition.stopLoss?.toString() ?? null,
        };
        
        useAccountStore.getState().updatePositionFromHttp(updatedPosition);
      } else {
        // Full close - remove position from store IMMEDIATELY
        // This makes the position disappear from the table instantly
        // If the API call fails, WebSocket will restore correct state
        removePosition(data.market);
      }

      try {
        // Place an opposite market order to close
        // Using reduce_only=true to skip margin checks (closing doesn't need margin)
        const closeSide = data.side === "long" ? "short" : "long";
        const request = toBackendOrderRequest(
          data.market,
          closeSide,
          data.size,
          data.currentPrice, // Market order uses current price
          "market",
          { reduceOnly: true } // This is a close order - skip margin requirements
        );

        const order = await OrdersApi.placeOrder(request);
        
        // Use backend's realized P&L if available (most accurate - uses actual execution price)
        // Backend calculates P&L using the actual fill price, not the stale currentPrice from UI
        // Fallback to frontend calculation only if backend doesn't provide it
        const realizedPnl = order.realized_pnl 
          ? parseFloat(order.realized_pnl)
          : (() => {
              // Fallback: calculate from UI data (may be slightly inaccurate due to stale price)
              const entryValue = data.size * data.entryPrice;
              const exitValue = data.size * data.currentPrice;
              return data.side === "long" 
                ? exitValue - entryValue 
                : entryValue - exitValue;
            })();

        // Apply order result to store (for order history)
        applyMutationResult({ order: order.order });
        
        // NO query invalidation - store is already updated, WebSocket keeps it in sync
        // This prevents the "whole panel reloading" issue

        return {
          success: true,
          realizedPnl,
          txSignature: order.order.id,
        };
      } catch (error) {
        console.error("[useBackendPositions] Close failed:", error);
        // On error, WebSocket will restore the position if it still exists
        return {
          success: false,
          error: error instanceof Error ? error.message : "Failed to close position",
        };
      }
    },
  });

  // Refetch function - fetch positions from HTTP and sync to store
  // Used after mutations like leverage change to ensure UI is up-to-date
  const syncFromHttp = useAccountStore.getState().syncFromHttp;
  
  const refetch = useCallback(async () => {
    if (!userId) return;
    
    try {
      // Fetch positions, orders, and account in parallel
      const [positions, orders, accountResponse] = await Promise.all([
        PositionsApi.listPositions().catch(() => []),
        OrdersApi.listOrders().catch(() => []),
        AccountsApi.getAccount().catch(() => null),
      ]);
      
      // Sync to store - this will trigger UI updates
      syncFromHttp({
        account: accountResponse || undefined,
        positions,
        orders,
      });
    } catch (error) {
      console.error("[useBackendPositions] Refetch failed:", error);
    }
  }, [userId, syncFromHttp]);

  return {
    positions,
    rawPositions: [], // Deprecated - use positions instead
    // Only show loading if there's a user to load data for
    // Without user: not loading (will show "No open positions" empty state)
    isLoading: userId ? (isLoading || !isInitialized) : false,
    error: null as Error | null,
    refetch,
    closePosition: closePositionMutation.mutate,
    closePositionAsync: closePositionMutation.mutateAsync,
    isClosing: closePositionMutation.isPending,
    isWsConnected: isInitialized, // Store is connected when initialized
  };
};

// Re-export the position type for backwards compatibility
export type { StorePosition as BackendPosition };
